<script setup lang="ts">
import type { Schemas } from "#shopware";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "~/lib/validator/validators";

const product = inject<Ref<Schemas["Product"]>>("product");
if (!product) {
	throw new Error("No product provided");
}

const isOpen = ref<boolean>(false);

const formValues = useState(() => ({
	title: "",
	content: "",
	name: "",
	email: "",
	points: "5"
}));

const validator = useVuelidate(
	{
		title: { required },
		content: { required },
		name: { required },
		email: { required, email }
	},
	formValues
);

const { apiClient } = useShopwareContext();

const onSubmit = async () => {
	await validator.value.$validate();
	if (validator.value.$error) {
		return;
	}

	await apiClient.invoke("saveProductReview post /product/{productId}/review", {
		pathParams: { productId: product.value.id },
		body: {
			...formValues.value,
			points: parseInt(formValues.value.points)
		}
	});

	isOpen.value = false;
	formValues.value = {
		title: "",
		content: "",
		name: "",
		email: "",
		points: "5"
	};
};
</script>

<template>
	<KippieButton block color="white" @click="isOpen = true" size="small">Schrijf een review</KippieButton>
	<ClientOnly>
		<KippieSidebar v-model="isOpen" color="white" placement="right">
			<template #top>
				<Typography variant="h6">Schrijf een review</Typography>
			</template>

			<div class="flex flex-col gap-6">
				<Divider />

				<FormField label="Algemene score">
					<KippieRatingInput v-model="formValues.points" />
				</FormField>

				<Divider />

				<KippieInput
					v-model="formValues.title"
					label="Titel van de review"
					required
					:error="validator.title.$error"
					:help="validator.title.$errors.map((e) => e.$message).join(', ')"
				/>

				<KippieInput
					type="textarea"
					v-model="formValues.content"
					label="Inhoud van de review"
					required
					:rows="3"
					:error="validator.content.$error"
					:help="validator.content.$errors.map((e) => e.$message).join(', ') ?? 'Voorbeeld: Heerlijke pan'"
				/>

				<KippieInput
					v-model="formValues.name"
					label="Jouw naam"
					required
					:error="validator.name.$error"
					:help="validator.name.$errors.map((e) => e.$message).join(', ')"
				/>

				<KippieInput
					type="email"
					v-model="formValues.email"
					label="E-mailadres"
					required
					:error="validator.email.$error"
					:help="validator.email.$errors.map((e) => e.$message).join(', ')"
				/>
			</div>

			<template #bottom>
				<div class="bg-sand flex gap-2 p-4 -m-5">
					<KippieButton block size="small" @click="onSubmit">Opslaan</KippieButton>
				</div>
			</template>
		</KippieSidebar>
	</ClientOnly>
</template>
